import type { PricingCampaignModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { Fragment, useState } from 'react'
import { any, pipe, prop, propOr, size, uniq } from 'lodash/fp'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePricingCampaignQuery } from '@/entities/pricing-campaign'

import { Blanket } from '@/shared/components/ui/blanket'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { Loader } from '@/shared/components/ui/loader'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/shared/components/ui/table'

import { getKeyTitle } from '../lib/helpers'

type SettingNode = {
	key: string
	label: string
	preview: string | number | null
	children: SettingNode[]
}
const toggleSetItems = (set: string[], items: string[]) => {
	const setArr = [...set]
	const isAllSelected = !any((i) => !setArr.includes(i), items)

	return isAllSelected
		? setArr.filter((i) => !items.includes(i))
		: uniq([...setArr, ...items])
}
const getSettings = (
	entity: PricingCampaignModel,
	t: TFunction
): SettingNode[] => [
	{
		key: 'settings.activity',
		label: t('settings.activity.short', { defaultValue: 'Activity' }),
		preview: null,
		children: [],
	},
	{
		key: 'settings.product_assignments',
		label: t('settings.assignment.short', { defaultValue: 'Assignment' }),
		preview: pipe([prop('settings.product_assignments'), size])(entity),
		children: [],
	},
	{
		key: 'settings.pricing_tactics',
		label: t('settings.tactics.short', { defaultValue: 'Tactics' }),
		preview: propOr('-', 'settings.pricing_tactics.engine', entity),
		children: [],
	},
	{
		key: 'settings.price_limits',
		label: t('settings.price_limits.short', { defaultValue: 'Price limits' }),
		preview: null,
		children: [
			{
				key: 'settings.price_limits.lower',
				label: t('price_limits_lower_title', {
					defaultValue: 'Lower price limit',
				}),
				preview: pipe([prop('settings.price_limits.lower'), size])(entity),
				children: [],
			},
			{
				key: 'settings.price_limits.upper',
				label: t('price_limits_upper_title', {
					defaultValue: 'Upper price limit',
				}),
				preview: pipe([prop('settings.price_limits.upper'), size])(entity),
				children: [],
			},
			{
				key: 'settings.price_limits.extra.on_conflict',
				label: t('price_limits_on_conflict_title', {
					defaultValue: 'On conflict',
				}),
				preview: prop('settings.price_limits.extra.on_conflict', entity),
				children: [],
			},
		],
	},
	{
		key: 'settings.anchoring',
		label: t('settings.anchoring.short', { defaultValue: 'Anchoring' }),
		preview: pipe([prop('settings.anchoring'), size])(entity),
		children: [
			{
				key: 'settings.anchoring.rules',
				label: t('anchoring_rules', { defaultValue: 'Rules' }),
				preview: pipe([prop('settings.anchoring.rules'), size])(entity),
				children: [],
			},
			{
				key: 'settings.anchoring.on_conflict',
				label: t('anchoring_on_conflict', { defaultValue: 'On conflict' }),
				preview: pipe([prop('settings.anchoring.on_conflict')])(entity),
				children: [],
			},
		],
	},
	{
		key: 'settings.rounding',
		label: t('settings.rounding.short', { defaultValue: 'Rounding' }),
		preview: pipe([prop('settings.rounding'), size])(entity),
		children: [],
	},
	{
		key: 'settings.minimal_steps',
		label: t('settings.minimal_steps.short', { defaultValue: 'Minimal steps' }),
		preview: pipe([prop('settings.minimal_steps'), size])(entity),
		children: [],
	},
]
export default function SelectSettingsView({
	pricingCmapaignId,
	selected,
	setSelected,
}: {
	pricingCmapaignId: string
	selected: string[]
	setSelected: (selected: string[]) => void
}) {
	const { t } = useTranslation()

	const [expanded, setExpanded] = useState<string[]>([])
	const pricingCampaignQuery = usePricingCampaignQuery(pricingCmapaignId, {
		select: (data) => getSettings(data, t),
	})

	return (
		<div className='relative min-h-30 overflow-x-auto rounded-lg border text-sm'>
			<Blanket
				className='absolute flex items-center justify-center rounded-lg bg-white/50 backdrop-blur-lg dark:bg-black/50'
				isTinted={pricingCampaignQuery.isLoading}
			>
				<Loader />
			</Blanket>
			<Table className='w-full table-auto overflow-hidden rounded-lg text-left'>
				<colgroup>
					<col
						style={{
							width: 34,
						}}
					/>
					<col
						style={{
							width: 34,
						}}
					/>
					<col
						style={{
							width: 480,
						}}
					/>
				</colgroup>
				<TableHeader className='text-xs'>
					<TableRow>
						<TableHead scope='col' className='p-2 pr-0' />
						<TableHead scope='col' className='p-2 pr-0' />
						<TableHead scope='col' className='p-2'>
							Setting
						</TableHead>
						<TableHead scope='col' className='p-2'>
							Value
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{pricingCampaignQuery.data?.map((settingNode) => {
						const isSelected = selected.includes(settingNode.key)
						const ieExpandable = settingNode.children?.length > 0
						const isExpanded = expanded.includes(settingNode.key)
						const handleExpandChange = () =>
							setExpanded(toggleSetItems(expanded, [settingNode.key]))
						const handleSelectChange = () =>
							setSelected(toggleSetItems(selected, [settingNode.key]))

						return (
							<Fragment key={settingNode.key}>
								<TableRow className='border-b'>
									<TableCell
										scope='row'
										className='p-2 pr-0 whitespace-nowrap dark:text-white'
									>
										<Checkbox
											checked={isSelected}
											onChange={handleSelectChange}
										/>
									</TableCell>
									<TableCell
										scope='row'
										className='p-2 pr-0 whitespace-nowrap dark:text-white'
									>
										{ieExpandable ? (
											isExpanded ? (
												<ChevronDownIcon
													className='size-4 cursor-pointer'
													onClick={handleExpandChange}
												/>
											) : (
												<ChevronRightIcon
													className='size-4 cursor-pointer'
													onClick={handleExpandChange}
												/>
											)
										) : null}
									</TableCell>
									<TableCell className='p-2 font-medium'>
										<div className='flex items-center'>
											{t(getKeyTitle(settingNode.label), {
												defaultValue: settingNode.label,
											})}
										</div>
									</TableCell>
									<TableCell className='p-2'>{settingNode.preview}</TableCell>
								</TableRow>
								{ieExpandable &&
									isExpanded &&
									settingNode.children.map((settingsChild) => (
										<TableRow key={settingsChild.key} className='border-b'>
											<TableCell
												scope='row'
												className='p-2 whitespace-nowrap dark:text-white'
											/>
											<TableCell
												scope='row'
												className='p-2 whitespace-nowrap dark:text-white'
											/>
											<TableCell className='p-2 pl-6'>
												{t(getKeyTitle(settingsChild.label), {
													defaultValue: settingsChild.label,
												})}
											</TableCell>
											<TableCell className='p-2'>
												{settingsChild.preview}
											</TableCell>
										</TableRow>
									))}
							</Fragment>
						)
					})}
				</TableBody>
			</Table>
		</div>
	)
}
